/**
 * routes.tsx
 *
 * All route guard components in one place.
 * Use these as layout routes in App.tsx to protect sections of the app.
 * ProtectedRoute  — supplier-only (individual, agency, workers)
 * AdminRoute      — admin role only
 * PartnerRoute    — partner agents only
 * RoleRoute       — restrict a specific page to a subset of supplier roles
 */

import { ReactNode, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@mondivago/supabase';
import { useSupplierProfile } from '@/hooks/suppliers';
import { usePartner } from '@/hooks/partners';
import { LoadingSpinner } from '@/components/ui';
import { setItem, ONBOARDING_COMPLETED_KEY_PREFIX } from '@/shared/lib/localStorage';

// ─── Shared loading screen ────────────────────────────────────────────────────
const Loading = () => (
  <div className="flex h-screen items-center justify-center bg-white">
    <LoadingSpinner />
  </div>
);

// ─── ProtectedRoute ───────────────────────────────────────────────────────────
/** Protects supplier routes. Relies on `useAuth().isSupplier` (resolved by auth.tsx). */
export function ProtectedRoute() {
  const { user, isSupplier, isPartner, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useSupplierProfile();

  const onboardingCompleted = profile?.onboarding_completed ?? false;

  useEffect(() => {
    if (user && isSupplier && onboardingCompleted) {
      setItem(`${ONBOARDING_COMPLETED_KEY_PREFIX}${user.id}`, 'true');
    }
  }, [user, isSupplier, onboardingCompleted]);

  if (authLoading || (user && profileLoading)) return <Loading />;
  if (!user) return <Navigate to="/" replace />;
  // Partner with supplier access → let them in; pure partners → redirect to partner area
  if (isPartner && !isSupplier) return <Navigate to="/partner" replace />;
  if (!isSupplier) return <Navigate to="/" replace />;

  return <Outlet />;
}

// ─── AdminRoute ───────────────────────────────────────────────────────────────
/** Allows only users with role === 'admin'. */
export function AdminRoute() {
  const { user, role, loading } = useAuth();

  if (loading) return <Loading />;
  if (!user || role !== 'admin') return <Navigate to="/" replace />;

  return <Outlet />;
}

// ─── PartnerRoute ────────────────────────────────────────────────────────────
/**
 * Protects /partner/* routes. Checks if the user has a partner record in the DB.
 * If not → redirect to /partner/account-settings to complete profile.
 */
export function PartnerRoute() {
  const { user, loading: authLoading } = useAuth();
  const { loading: partnerLoading } = usePartner(user?.id);

  if (authLoading || partnerLoading) return <Loading />;
  if (!user) return <Navigate to="/partner/login" replace />;

  return <Outlet />;
}

// ─── RoleRoute ────────────────────────────────────────────────────────────────
/**
 * Restricts a page to specific supplier sub-roles (e.g. individual, agency).
 * Wrap individual <Route> elements inside <RoleRoute allowedRoles={[...]}>
 */
interface RoleRouteProps {
  allowedRoles: string[];
  children: ReactNode;
}

export function RoleRoute({ allowedRoles, children }: RoleRouteProps) {
  const { role, loading } = useAuth();

  if (loading) return <Loading />;
  if (!role || !allowedRoles.includes(role)) return <Navigate to="/supplier/dashboard" replace />;

  return <>{children}</>;
}
